/**
 * DecisionFlow — Auth Context
 * Stage 8 Job A · A.2
 * M041 Phase 2 · S223 · May 2026
 * S280: loginWithSession() added for SSO callback flow.
 *
 * Holds the current-user state and exposes login/logout. Wraps all routes.
 *
 * On app mount: tries GET /api/v1/auth/me. If cookie is valid → user set.
 * If not → user is null, protected routes redirect to /login.
 *
 * M041 Phase 2 boot sequence (runs inside login() after successful auth):
 *   1. POST /api/v1/auth/login         → session cookie set
 *   2. GET  /api/v1/tenant-context     → locale_code + locale fields
 *   3. GET  /api/v1/tenant-strings     → [ { namespace, key, value, locale_code } ]
 *   4. applyTenantOverrides()          → merge into live i18next bundle
 *   5. setUser() + setLocale()         → render authenticated shell
 *
 * SSO boot sequence (runs inside loginWithSession() after SSO callback):
 *   1. POST /api/v1/auth/sso/callback  → session cookie set (SsoCallback.jsx)
 *   2. GET  /api/v1/auth/me            → user profile (SsoCallback.jsx)
 *   3. loginWithSession(user)          → sets user state, triggers shell render
 *   Tenant i18n: best-effort, same as password login.
 */

import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiLogin, apiLogout, apiMe, apiGet, AuthExpiredError } from "../api/client.js";
import { applyTenantOverrides } from "../i18n/index.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]     = useState(null);
  const [booted, setBooted] = useState(false);
  const [error, setError]   = useState(null);
  const [locale, setLocale] = useState(null); // resolved locale_code from tenant-context

  // Initial session probe: is there a valid cookie already?
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await apiMe();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setBooted(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      // Step 1: authenticate — sets session cookie.
      const res = await apiLogin(email, password);

      // Steps 2–4: fetch tenant locale + string overrides, inject into i18n bundle.
      // Both fetches are best-effort — a failure must not block login.
      try {
        const [contextData, stringsData] = await Promise.all([
          apiGet("/api/v1/tenant-context"),
          apiGet("/api/v1/tenant-strings"),
        ]);

        const localeCode = contextData?.locale?.locale_code ?? "en-US";

        // Build per-namespace override map from flat tenant-strings array.
        // Response envelope key is "data" per contract.
        const overrideList = stringsData?.data ?? [];
        const overrideMap = {};
        overrideList.forEach(({ namespace, key, value }) => {
          if (!overrideMap[namespace]) overrideMap[namespace] = {};
          overrideMap[namespace][key] = value;
        });

        // Merge tenant overrides into the live i18next bundle.
        applyTenantOverrides(localeCode, overrideMap);

        // Step 5: store resolved locale in context.
        setLocale(localeCode);
      } catch (i18nErr) {
        // Non-fatal — log and continue. App renders with en.json base strings.
        console.warn("[AuthContext] tenant i18n fetch failed — using base strings:", i18nErr);
        setLocale("en-US");
      }

      // Step 5 cont: set user last — triggers authenticated shell render.
      setUser(res.user);
      return { ok: true, user: res.user };
    } catch (e) {
      setError(e);
      return { ok: false, error: e };
    }
  }, []);

  /**
   * loginWithSession — SSO callback entry point (S280).
   *
   * Called by SsoCallback.jsx after:
   *   1. POST /api/v1/auth/sso/callback has set the httpOnly session cookie
   *   2. GET  /api/v1/auth/me has returned the authenticated user profile
   *
   * Sets user state and attempts tenant i18n load (best-effort, same as
   * password login). Does NOT call apiLogin — cookie is already set.
   *
   * @param {object} userProfile — user object from apiMe() response
   * @returns {{ ok: true, user: object }}
   */
  const loginWithSession = useCallback(async (userProfile) => {
    setError(null);
    try {
      // Best-effort tenant i18n — same pattern as password login.
      try {
        const [contextData, stringsData] = await Promise.all([
          apiGet("/api/v1/tenant-context"),
          apiGet("/api/v1/tenant-strings"),
        ]);

        const localeCode = contextData?.locale?.locale_code ?? "en-US";
        const overrideList = stringsData?.data ?? [];
        const overrideMap = {};
        overrideList.forEach(({ namespace, key, value }) => {
          if (!overrideMap[namespace]) overrideMap[namespace] = {};
          overrideMap[namespace][key] = value;
        });
        applyTenantOverrides(localeCode, overrideMap);
        setLocale(localeCode);
      } catch (i18nErr) {
        console.warn("[AuthContext] SSO tenant i18n fetch failed — using base strings:", i18nErr);
        setLocale("en-US");
      }

      // Set user — triggers authenticated shell render.
      setUser(userProfile);
      return { ok: true, user: userProfile };
    } catch (e) {
      setError(e);
      return { ok: false, error: e };
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
    setLocale(null);
  }, []);

  // Session-expired helper — components can call this on AuthExpiredError.
  const handleSessionExpired = useCallback(() => {
    setUser(null);
    setLocale(null);
  }, []);

  const value = {
    user,
    booted,
    error,
    locale,
    login,
    loginWithSession,
    logout,
    handleSessionExpired,
    isCompAdmin: user?.user_type === "comp_admin",
    isFinance:   user?.user_type === "finance",
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Helper hook for screens: wraps an async fn so any AuthExpiredError
 * clears the session and redirects to /login. Use for all data-fetching
 * screens.
 */
export function useApiCall() {
  const { handleSessionExpired } = useAuth();
  const navigate = useNavigate();

  return useCallback(async (fn) => {
    try {
      return await fn();
    } catch (e) {
      if (e instanceof AuthExpiredError) {
        handleSessionExpired();
        navigate("/login", { replace: true });
      }
      throw e;
    }
  }, [handleSessionExpired, navigate]);
}
